/**
 * All user-facing copy for Revenue Attribution.
 *
 * Kept in one file on purpose — this text is expected to be revised, and revising
 * it should never mean touching component code.
 */

export const COPY = {
    page: {
        title: "Revenue Attribution",
        subtitle: "See what your AI visibility is actually worth."
    },

    /**
     * "How attribution works" explainer, pinned to the top of the tab.
     *
     * Copy rules for this block: never say predict / estimate / projected — phase 1
     * shows measured data only and this component sets that expectation. Never imply
     * per-deal or per-user attribution. Say "AI assistant", not LLM or AI engine, so
     * the wording lines up with GA4's own channel naming.
     */
    howItWorks: {
        title: "How attribution works",
        modalSubtitle: "All four steps, and how a cited answer becomes measured revenue.",

        steps: [
            {
                label: "Someone asks an AI assistant",
                body: "A buyer asks ChatGPT or Perplexity for recommendations in your category"
            },
            {
                label: "Your page gets cited",
                body: "We already track which prompts cite which of your pages"
            },
            {
                label: "They visit and convert",
                body: "GA4 records the session, landing page, and conversion"
            },
            {
                label: "You see what it earned",
                body: "We join both sides: which cited pages actually drive revenue"
            }
        ],
        /** Step 2 is the part only CitedBy can do. */
        citedByTag: "CitedBy",

        animation: {
            play: "Play animation",
            pause: "Pause animation",
            question: "best CDP for B2B SaaS",
            sources: ["vendor-a.com", "yourbrand.com", "vendor-b.com"],
            brand: "yourbrand.com",
            landingPage: "/compare/cdp-vs-cdw",
            sessionLabel: "session",
            valueLabel: "$400 / session"
        },

        requirements: {
            items: [
                { label: "Read-only GA4 access", time: "~2 min" },
                { label: "One key event marked in GA4", time: "~1 min" },
                { label: "Your average deal value", time: "~30 sec" }
            ],
            noCode: "No tracking code. No changes to your site."
        },

        honesty: {
            title: "What we can and can't see",
            paragraphs: [
                "We can show you that a visitor arrived from ChatGPT, which page brought them in, and what that page earns. We can't see the exact question they asked — no AI platform passes that.",
                "Most AI-influenced visits arrive with no referrer at all and are counted as Direct traffic. The numbers here are a floor, not a total."
            ],
            panelLink: "Why is my AI number so small?"
        },

        smallNumberPanel: {
            title: "Why is my AI number so small?",
            sections: [
                {
                    title: "Zero-click answers",
                    body: "Most people read the AI answer and never click through. You were cited; there was no visit to measure."
                },
                {
                    title: "Stripped referrers",
                    body: "Many AI platforms send visitors without a referrer header. Those sessions land in Direct and can't be distinguished from someone typing your URL."
                },
                {
                    title: "Google AI Overviews",
                    body: "Clicks from AI Overviews and AI Mode are reported by GA4 as Organic Search. There is no way to separate them."
                },
                {
                    title: "Answers with no visit at all",
                    body: "When an assistant answers from what it already knows, it never touches your site. The recommendation happened; nothing was recorded anywhere."
                }
            ],
            closing:
                "This is why we also track your citations directly — visibility is measurable even when the click isn't."
        }
    },

    /** §3 — NOT_CONFIGURED empty state */
    empty: {
        headline: "Connect revenue data",
        body: "See which AI-cited pages drive conversions, and how AI traffic compares to Direct and Organic. Takes about 5 minutes — no tracking code required.",
        previews: [
            "Traffic and conversion trends by channel",
            "Revenue per session by landing page",
            "Your cited pages, ranked by what they actually earn"
        ],
        primaryCta: "Set up attribution",
        secondaryLink: "What data do you need?"
    },

    /** §3 — side panel behind "What data do you need?" */
    requirementsPanel: {
        title: "What data do you need?",
        intro: "Three things at most, and none of them require a developer.",
        items: [
            {
                title: "Read-only Google Analytics access",
                body: "We request the read-only Analytics scope so we can pull sessions and conversions by landing page and channel. We never write to, modify, or delete anything in your Analytics property."
            },
            {
                title: "One key event",
                body: "At least one GA4 event marked as a key event — a demo request, a signup, a lead form. If you already report on conversions in GA4, you have this."
            },
            {
                // Mirrors the real behaviour: the revenue probe decides whether this
                // step is asked at all (hasGa4Revenue skips it).
                title: "An average deal value — only if GA4 has none",
                body: "If your property already reports revenue, we use those actual amounts and skip this entirely. If it doesn't, we ask what a conversion is worth on average so counts can be turned into revenue. An estimate is fine, and you can change it any time."
            }
        ],
        noCode: "No code changes. No tracking pixel. No CRM connection. Nothing to deploy."
    },

    /** Step 0 — which way the customer sets up, before any GA4 work. */
    pathChooser: {
        label: "Data source",
        heading: "Where is your revenue data?",
        subheading: "This decides how we connect. You can change it later.",
        /** Right-panel eyebrow — reframes the choice as "here's what you're installing". */
        detailLabel: "What you'll connect",
        detailLabelSoon: "In development",
        ga4: {
            title: "Google Analytics 4",
            tag: "Available now",
            sub: "Use the analytics you already have.",
            body: "You already track conversions in GA4. Connect read-only over Google OAuth and we take it from there — no code, no keys to paste, about 5 minutes.",
            bullets: [
                "Read-only access via Google OAuth — no property ID or keys to paste",
                "One connection per project — reconnect or disconnect anytime",
                "We backfill recent history, then keep it in sync every day"
            ]
        },
        revenueStack: {
            title: "ThriveStack",
            tag: "Coming soon",
            sub: "Read payments straight from Stripe.",
            body: "Revenue lives in Stripe, or you're not tracking conversions in GA4 yet. A lightweight ThriveStack script captures visits and their source, and we read payments straight from Stripe.",
            bullets: [
                "Actual payment amounts from Stripe, not an estimated deal value",
                "A lightweight script records each visit and where it came from",
                "No GA4 configuration needed"
            ]
        },
        pick: "Continue",
        /** The card can't be selected in live mode, but demand still gets captured. */
        requestFeature: "Request this",
        hideRequest: "Hide"
    },

    /** Phase 2 path: real interest capture in live mode, walkthrough in demo. */
    revenueStack: {
        comingSoon: {
            label: "Coming soon",
            heading: "Stripe + ThriveStack is in development",
            body: "This path reads payments directly from Stripe and attributes them with our own script, so you get real amounts instead of an estimated deal value. It isn't ready yet.",
            interestHeading: "Want this sooner?",
            interestBody:
                "Tell us how you'd use it. We build what gets asked for most, and we'll come back to you when it ships.",
            upvote: "Upvote this",
            upvoted: "Upvoted — thanks",
            useCasePlaceholder: "What would you want to measure? e.g. which AI-cited pages drive paid subscriptions.",
            submit: "Request feature",
            submitting: "Sending…",
            thanks: "Thanks — that's logged. We'll be in touch when this is ready.",
            needUseCase: "Add a sentence on your use case so this counts for more than a vote."
        },
        script: {
            label: "Install script",
            heading: "Add the ThriveStack script",
            subheading: "One tag in your site's <head>. This is what records the visit and its source.",
            copy: "Copy script",
            copied: "Copied",
            verify: "I've added it — verify",
            verifying: "Checking for traffic…",
            verified: "Script detected. Visits are being recorded."
        },
        sessionId: {
            label: "Map sessions",
            heading: "Stamp the session on the customer",
            subheading:
                "Set session_id in Stripe customer metadata when you create the customer. That's the join between a visit we recorded and a payment Stripe recorded — without it a charge can't be traced back to the page that earned it.",
            keyLabel: "Metadata key",
            keyValue: "session_id",
            snippetLabel: "When creating or updating the customer",
            copy: "Copy",
            copied: "Copied",
            verify: "Check for session_id",
            verifying: "Reading recent customers…",
            found: "Found session_id on recent customers. Payments can be attributed.",
            note: "Same idea as group_id in the Revenue Intelligence Stripe setup — a metadata key we read, never write."
        },

        stripe: {
            label: "Connect Stripe",
            heading: "Connect Stripe",
            subheading: "Read-only access to charges and subscriptions. We never move money.",
            connect: "Connect Stripe",
            connecting: "Opening Stripe…",
            connected: (account: string) => `Connected to ${account}`,
            scope: "Read-only: charges, subscriptions, customers"
        }
    },

    /** §4 — setup wizard */
    wizard: {
        title: "Set up revenue attribution",
        steps: ["Connect GA4", "AI channel check", "Conversion revenue", "What happens next"],
        back: "Back",
        next: "Continue",
        close: "Close",
        finish: "Finish setup",
        progress: (current: number, total: number) => `Step ${current} of ${total}`,
        percentComplete: (n: number) => `${n}% complete`,

        step1: {
            heading: "Connect Google Analytics",
            summary: "Sign in with Google and choose the properties to read from.",
            connectCta: "Sign in with Google",
            reassurance: "We request read-only access. We never modify your Analytics data.",
            connecting: "Opening…",
            wizardConnecting: "Connecting to Google Analytics…",
            pickerHeading: "Choose properties",
            pickerSubheading: "Revenue attribution reads from every property you select.",
            pickerSearchPlaceholder: "Search properties",
            uaTooltip: "Universal Analytics is not supported.",
            accountLabel: (id: string) => `Account ${id}`,
            propertyLabel: (id: string) => `Property ${id}`,
            sessionsLast30d: (n: number) => `${n.toLocaleString()} sessions last 30 days`,
            copyPropertyId: "Copy property ID",
            copiedPropertyId: "Copied",
            multiProperty: "Select every property you want attribution for. You can change this later in settings.",
            selectedCount: (n: number) => `${n} ${n === 1 ? "property" : "properties"} selected`,
            selectAtLeastOne: "Select at least one property to continue.",
            connectedAs: (email: string) => `Connected as ${email}`,
            connectedScope: "Read-only access to Google Analytics",
            // Second consent, named up front so it doesn't read as a repeat.
            authoriseSyncBody:
                "One more Google screen. The first connected Analytics so we could read your account; this one authorises the ongoing sync itself.",
            authoriseSync: "Authorise syncing",
            authorising: "Opening Google…",
            syncAuthorised: "Syncing authorised",
            reAuthorise: "Re-authorise syncing",
            disconnect: "Disconnect"
        },

        /** MOCK: simulated Google consent flow. Demo mode only — never contacts Google. */
        oauth: {
            demoPill: "DEMO — not a real sign-in",
            windowLabel: "Google sign-in",
            chooser: {
                title: "Choose an account",
                subtitle: "to continue to CitedBy",
                useAnother: "Use another account",
                useAnotherUnavailable: "Adding a new account isn't available in this demo."
            },
            consent: {
                title: "CitedBy wants access to your Google Account",
                allowIntro: "This will allow CitedBy to:",
                scopeTitle: "See and download your Google Analytics data",
                scopeBody: "View your GA4 properties, reports, and configuration. Read-only.",
                scopeToggle: "Show technical details",
                trustTitle: "Make sure you trust CitedBy",
                trustBody:
                    "You may be sharing sensitive info with this site or app. Learn about how CitedBy will handle your data by reviewing its privacy policies. You can always see or remove access in your Google Account.",
                cancel: "Cancel",
                confirm: "Continue"
            },
            processing: "Redirecting…",
            errors: {
                DENIED: {
                    message: "Access was not granted. We need read-only Analytics access to continue.",
                    action: "Try again"
                },
                POPUP_CLOSED: {
                    message: "The Google sign-in window was closed before finishing.",
                    action: "Try again"
                },
                NO_PROPERTIES: {
                    message:
                        "No GA4 properties found on this account. Try a different Google account, or check that you have at least Viewer access.",
                    action: "Switch account"
                },
                INSUFFICIENT_PERMISSION: {
                    message:
                        "This account doesn't have permission to read the property you need. Ask an Administrator for Viewer access in GA4 → Admin → Property access management.",
                    action: "Try again"
                }
            }
        },

        step2: {
            heading: "AI channel check",
            subheading: "We look at how this property classifies traffic from AI answer engines.",
            checking: "Checking your channel groups…",
            missingTitle: "No AI channel found in this property.",
            missingBody:
                "GA4 has no channel that captures AI answer engines, so every AI session is currently landing in Referral or Direct. Add one using the rule below.",
            brokenTitle: "Your AI channel is misconfigured.",
            brokenIntro: "Two problems detected:",
            brokenProblems: [
                "Your channel matches on **Default channel group** instead of **Source**. This captures Paid Search and Email instead of AI traffic.",
                "The channel sits below Referral in the ordering, so AI sessions are counted as Referral."
            ],
            okTitle: "AI channel configured correctly.",
            okBody: "AI sessions are being classified into their own channel. Moving on.",
            fixHeading: "Fix instructions",
            regexLabel: "Source matches regex",
            copyRegex: "Copy regex",
            copiedRegex: "Copied",
            fixSteps: [
                "GA4 → Admin → Data display → Channel groups",
                "Open your custom group → Add new channel → name it `AI Search`",
                "Condition: **Source** → *matches regex* → paste the above",
                "Add a second rule (OR): **Default channel group** → *exactly matches* → `AI Assistant`",
                "Reorder: place directly below Direct, above Paid Search / Organic / Referral"
            ],
            orderingCallout:
                "Ordering matters. GA4 stops at the first match — below Referral, the channel reads zero regardless of the regex.",
            recheck: "I've fixed it — recheck",
            rechecking: "Rechecking…",
            skip: "Skip for now"
        },

        step3: {
            heading: "Which events count as conversions?",
            subheading: "These are the key events we found in your property over the last 90 days.",
            emptyTitle: "No key events marked in GA4.",
            emptyBody: "Mark at least one event as a key event in GA4 → Admin → Events. Takes two clicks, no code.",
            recheck: "Recheck",
            rechecking: "Rechecking…",
            atLeastOne: "Select at least one event to continue."
        },

        step4: {
            heading: "What's a conversion worth?",
            subheading: "We use this to convert counts into revenue. An estimate is fine — you can change it later.",
            currencyLabel: "Currency",
            helper: "Not sure? Divide annual new revenue by number of deals closed.",
            advanced: "Advanced",
            modelLabel: "Attribution model",
            modelValue: "First landing page (single-touch)",
            modelTooltip:
                "The page that acquired the visitor gets the credit. Pages like /pricing and /demo appear in most journeys and would dominate any other model.",
            lookbackLabel: "Lookback window",
            ga4RevenueTitle: "Revenue found in GA4.",
            ga4RevenueBody: "We'll use actual purchase values instead of an estimated deal value."
        },

        step5: {
            heading: "Group your pages.",
            subheading: "Individual pages rarely have enough conversions for a stable rate, so we group them by type.",
            typeHeader: "Type",
            patternsHeader: "URL patterns",
            addRow: "Add page type",
            matchSummary: (matched: number, total: number, other: number) =>
                `Matches ${matched.toLocaleString()} of ${total.toLocaleString()} pages. ${other.toLocaleString()} fall into Other.`
        }
    },

    /** §2 — SYNCING. Never say real-time, live, or instant anywhere in this flow. */
    syncing: {
        // Setup is done — this screen is the first-time load while the first import runs.
        eyebrow: "Setup complete",
        title: "We're building your report",
        lede: "Your Google Analytics is connected. We're importing your data and building your attribution model now — this is a one-time setup.",
        // An honest range beats a progress bar that stalls at 80%.
        started: (started: string) => `Started ${started}`,

        // Only the phases we can honestly light from live signals: setup saved
        // (connected), the first sync running (importing), and data landing (ready).
        // No "building the model" row — live has no distinct signal for it.
        phases: {
            connected: "Connected to Google Analytics",
            importing: "Importing your Analytics data",
            ready: "Report ready"
        },

        // The three things a first-time customer needs to know while they wait.
        timing: {
            label: "This takes a little while",
            body: "Most properties are ready in 10–30 minutes. Larger ones can take a few hours."
        },
        notify: {
            label: "We'll let you know",
            withEmail: (email: string) => `We'll email ${email} the moment your report is ready.`,
            withRecipients: "We'll email everyone below the moment your report is ready.",
            noEmail: "We'll email you the moment your report is ready — and this page updates on its own.",
            addPeople: "Add people",
            placeholder: "name@company.com",
            add: "Add",
            invalid: "Enter a valid email address."
        },
        leave: {
            label: "No need to wait here",
            body: "You can close this tab and come back anytime. Nothing will be lost."
        },

        // Shown once the sync crosses the slow threshold — reassure, don't alarm.
        slow: "This one's taking longer than usual — larger properties can take a few hours. You don't need to wait: we'll email you as soon as it's ready.",

        previewLabel: "A preview of what's coming"
    },

    /** Final wizard step — sets expectations before anyone sees an empty report. */
    whatsNext: {
        heading: "You're connected",
        summary: "When your data will appear, and what to expect.",
        steps: [
            {
                title: "First sync starts now",
                body: "We pull your last 30 days of GA4 data to start, then keep it current. The first sync usually takes 10–30 minutes depending on site size."
            },
            {
                title: "Then once every 24 hours",
                body: "New data appears the following day. This isn't real-time — attribution is a trend view, not a live dashboard."
            },
            {
                title: "Recent days will keep adjusting",
                body: "Google finalises data over 24–48 hours, so the last two days will shift slightly before settling."
            }
        ],
        noteTitle: "We start from your last 30 days.",
        noteBody:
            "Attribution begins with the last 30 days of GA4 data and builds forward from there. Traffic older than that isn't included.",
        // Turns a limitation into a reason not to delay, and it happens to be true.
        noteClosing: "The sooner this runs, the more history you'll have.",
        startSync: "Create connection",
        starting: "Creating connection…",
        secondConsent: "Syncing was authorised on step 1. This creates the connection and starts the first sync.",
        started: (at: string) => `Sync started ${at}`,
        alreadyConnected: "This property was already connected — we've queued a fresh sync.",
        startFirst: "Start the sync to begin collecting.",
        cta: "Go to report"
    },

    /** Airbyte's secret handle went stale mid-wizard. */
    secretExpired: {
        title: "Authorisation expired",
        body: "Google's authorisation for syncing is only valid for a short time and this one has lapsed. Re-authorise to carry on — you'll come straight back to this step and nothing you've entered is lost.",
        cta: "Re-authorise syncing"
    },

    /** Beta notice — mirrors the ChatGPT Ads banner pattern. */
    beta: {
        pill: "Beta",
        title: "Revenue Attribution is free during beta",
        body: "Full access is included at no cost while we refine this experience. Paid plans may be introduced later — we'll give you advance notice before any pricing changes take effect.",
        dismiss: "Dismiss beta notice"
    },

    /**
     * Connection settings. The Property ID lives here permanently — once setup is
     * done there is nowhere else a customer can go to read back the number support
     * will ask them for.
     */
    connection: {
        title: "Attribution connection",
        openLink: "Manage connection",
        account: {
            heading: "Google account",
            scope: "Read-only access to Google Analytics",
            disconnect: "Disconnect"
        },
        property: {
            heading: "Properties",
            name: "Name",
            id: "Property ID",
            account: "GA4 account",
            timeZone: "Reporting timezone",
            currency: "Currency",
            // GA4's own zone decides day boundaries; a common off-by-one confusion.
            timeZoneNote: "Day boundaries follow the property's timezone, not yours.",
            change: "Change properties"
        },
        sync: {
            heading: "Syncing",
            cadence: "Every 24 hours"
        },
        attribution: {
            heading: "Attribution",
            model: "Model",
            lookback: "Lookback window",
            lookbackValue: (days: number) => `${days} days`
        },
        conversions: {
            heading: "Conversion events",
            empty: "No events selected."
        },
        pageTypes: {
            heading: "Page types",
            other: "Other",
            otherPatterns: "everything else"
        },
        notConnected: "No Analytics property is connected yet."
    },

    /** §4 — sync status. "data through" ≠ "last sync"; never swap them. */
    sync: {
        indicator: (updated: string, next: string) => `Updated ${updated} · next sync ${next}`,
        popover: {
            lastSync: "Last successful sync",
            nextSync: "Next scheduled sync",
            dataThrough: "Data through",
            property: "Property",
            properties: "Properties",
            yourTime: "(your time)",
            note: "Data for the last 2 days may still change as Google finalises it."
        },
        failedPill: (when: string) => `Sync failed ${when}`,
        failurePanelTitle: "Syncing has stopped",
        failures: {
            TOKEN_REVOKED: {
                message: "Google access was revoked. Reconnect to resume syncing.",
                action: "Reconnect"
            },
            PERMISSION_REMOVED: {
                message: (propertyId: string) =>
                    `This account no longer has access to property ${propertyId}. Ask an Administrator to restore Viewer access in GA4 → Admin → Property access management.`,
                action: null
            },
            QUOTA_EXCEEDED: {
                message: "Google's daily data limit was reached. Syncing resumes automatically tomorrow.",
                action: null
            },
            PROPERTY_DELETED: {
                message: "This property no longer exists in Google Analytics.",
                action: "Choose another property"
            }
        },
        // A wrong number presented confidently is worse than no number.
        staleBanner: (date: string) =>
            `These numbers are from ${date}. Syncing has been interrupted — figures may be out of date.`
    },

    /** §5 — report */
    report: {
        editSetup: "Edit setup",
        dateRangeLabel: "Date range",
        channelFilterLabel: "Channels",
        stats: {
            sessions: "AI Sessions",
            conversions: "AI Conversions",
            revenue: "AI Revenue",
            revPerSession: "Rev / Session",
            vsPrev: "vs prev period",
            vsOrganic: (value: string) => `vs ${value} Organic`
        },
        trend: {
            title: "Channel trend",
            subtitle: "Weekly buckets across AI Search, Direct and Organic.",
            metrics: { sessions: "Sessions", conversions: "Conversions", revenue: "Revenue" },
            indexToggle: "Index to 100",
            indexHint: "Tip: index the view to compare growth rates.",
            sourceMixLabel: "AI sources",
            noChannels: "Select at least one channel to plot.",
            olderWeeks: "Show earlier weeks",
            newerWeeks: "Show later weeks"
        },
        pageTable: {
            title: "Page performance",
            subtitle: "AI-sourced sessions by landing page, ranked by revenue.",
            columns: {
                page: "Page",
                type: "Type",
                sessions: "AI Sessions",
                conversions: "Conv",
                cvr: "CVR",
                valuePerSession: "$/Session",
                revenue: "Revenue",
                citedOn: "Cited on"
            },
            citedOn: (count: number) => `${count} ${count === 1 ? "prompt" : "prompts"}`,
            notCited: "Not cited",
            expandPrompts: "Prompts citing this page",
            // One tooltip per column — each explains only its own number.
            sessionsTooltip:
                "Sessions that arrived from an AI assistant and landed on this page. A floor, not a total — most AI-influenced visits arrive without a referrer and are counted as Direct.",
            conversionsTooltip:
                "Key events recorded on this page's AI sessions. AI is roughly 1% of site traffic, so these counts are small and often zero on a page that still earns.",
            cvrTooltip: "Conversion rate. Key events ÷ sessions for this page, across all traffic.",
            valuePerSessionTooltip:
                "Modeled from this page's conversion rate across all traffic, applied to your stated deal value. $/session = CVR × deal value.",
            revenueTooltip: "AI sessions × $/session for this page.",
            citedOnTooltip:
                "Tracked prompts whose AI answer cites this page. This is the join between your visibility data and your revenue data.",
            noObservedConversions: "No AI conversion recorded in this window.",
            empty: "No AI-sourced sessions for the selected filters."
        },
        pageTypes: {
            title: "Value by page type",
            subtitle: "Revenue per AI session, grouped by the page types you defined."
        }
    },

    /** States that arrive as 200 bodies from the report endpoints. */
    reportStates: {
        noData: {
            title: "No data in this range",
            body: "The connection is working — there just aren't any AI-sourced sessions in the dates you've selected. Try a wider range."
        },
        stale: (dataThrough: string) =>
            `Data through ${dataThrough}. Syncing is running behind, so recent days are missing.`,
        lowSample: (threshold: number) =>
            `Fewer than ${threshold.toLocaleString()} conversions in this window. These figures are correct but thin — treat them as directional.`,
        brandedSearch:
            "The Organic baseline still includes branded search, so this multiple is understated — the real gap is wider.",
        excludedSessions: (n: number) =>
            `${n.toLocaleString()} sessions fall outside these three channels and aren't plotted.`,
        badRequest: "Something is wrong with this request. This is a bug on our side, not your setup.",
        // Live mode must never borrow the demo fixtures — an invented number
        // presented as the customer's own is worse than an empty panel.
        notFetched: "No data fetched.",
        notFetchedBody: "Nothing came back from the API for this range yet."
    },

    /** Page performance states, all delivered as 200 bodies. */
    pageStates: {
        noAiTraffic: {
            title: "No AI traffic in this range",
            body: "The connection is working — no AI assistant sent anyone to your site in these dates. Try a wider range."
        },
        revenueNotConfigured:
            "Revenue isn't configured yet, so the money columns are empty and can't be sorted. Add a conversion value in setup.",
        otherBucket:
            'GA4 collapsed some landing pages into an "(other)" bucket, so this list is incomplete. Narrow the date range to see more of them.',
        citationsUnavailable: "Citation data is temporarily unavailable, so that column is hidden.",
        lowSample: (threshold: number) =>
            `Fewer than ${threshold.toLocaleString()} conversions on this page, so $/session is modelled from its page-type rate rather than the page itself. The value is still shown.`,
        citationSortScoped: "Sorting by citations orders only the page you're looking at, not the whole list.",
        unattributed: (conversions: number, pages: number) =>
            `${conversions.toLocaleString()} conversions couldn't be tied to a landing page${
                pages > 0 ? `, and ${pages.toLocaleString()} pages are excluded` : ""
            } — which is why this table won't add up to the cards above.`,
        totalsLabel: "Totals",
        rowsRange: (from: number, to: number, total: number) =>
            `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`,
        prev: "Previous page",
        next: "Next page",
        allPageTypes: "All page types",
        allVendors: "All sources",
        viewAllPrompts: (n: number) => `View all ${n.toLocaleString()} prompts`,
        revenueSource: {
            ga4_native: "From GA4's own revenue.",
            modeled: "Modelled from this page's conversion rate and your stated deal value.",
            none: "No revenue could be derived for this page."
        }
    },

    /** §6 — honesty and confidence */
    honesty: {
        floorBanner:
            "Showing AI traffic that arrives with a referrer. Most AI-influenced visits arrive without one and are counted as Direct. This is a floor, not a total.",
        floorBannerLink: "Learn more",
        floorPanel: {
            title: "Why this is a floor, not a total",
            sections: [
                {
                    title: "Referrer stripping",
                    body: "Several AI clients — desktop apps, mobile apps, and some browser configurations — send visitors without a referrer at all. Those sessions arrive as Direct and are indistinguishable from someone typing your URL."
                },
                {
                    title: "Google AI Overviews count as Organic",
                    body: "When an AI Overview cites you and the visitor clicks through, GA4 sees a normal google.com organic session. There is no signal that separates it from a blue-link click."
                },
                {
                    title: "No platform passes the prompt",
                    body: "No AI answer engine passes the user's question in the referrer or query string. We can tell you a session came from ChatGPT; we cannot tell you what was asked. That join comes from your tracked prompts, not from Analytics."
                }
            ],
            closing:
                "Everything in this report is measured, not modeled from unobserved traffic. When AI's real contribution is larger than what's shown here, we would rather under-report it than guess."
        },
        lowSampleTooltip: "Not enough conversions for a reliable rate. Using site average.",
        skippedFixBanner:
            "Your GA4 AI channel may be misconfigured — these numbers could include Paid Search and Email traffic.",
        skippedFixCta: "Fix now",
        attributionModelLabel: "Revenue attributed to first landing page of the visitor's journey."
    },

    /** §7 — exactly two upgrade prompts. Do not add more. */
    upgrades: {
        history: "Showing 90 days. Connect ThriveStack for continuous history →",
        stripe: "Connect Stripe for actual per-page revenue →"
    },

    /** Non-demo blur + overlay (repo convention). */
    nonDemo: {
        title: "Revenue Attribution",
        body: "Connect Google Analytics to see what your AI visibility earns. Switch to Demo Mode to preview with sample data.",
        cta: "Preview with Demo Data"
    }
};

/** §4 Step 2 — the GA4 source regex customers copy into their channel group. */
export const AI_SOURCE_REGEX =
    "chatgpt\\.com|chat\\.openai\\.com|openai\\.com|perplexity\\.ai|gemini\\.google\\.com|copilot\\.microsoft\\.com|edgeservices\\.bing\\.com|claude\\.ai|you\\.com|deepseek\\.com|grok\\.com|meta\\.ai|mistral\\.ai";
